<template>
  <FitHalfScreenDialog
    :appendToBody="true"
    :title="i18n('優惠券詳情')"
    :visible="isShow"
    :closeAriaLabel="i18n('關閉')"
    @onClose="handleClose"
    class="counpon-detail-dialog"
    @click.native.stop
    @touchmove.native.prevent
    ref="dialog"
  >
    <div
      slot="bd"
      class="counpon-detail-content"
      v-if="couponInfo"
      v-stat.brow_mult="{
        tag: 'wecoupon_module.coupondetail_view|wecoupons-券详情-浏览',
        ext: {
          fcouponid: couponInfo.couponId,
          fspid: couponInfo.spId,
          fstate: couponState,
          ...getBiExt(),
        },
      }"
    >
      <div class="merchant-info-slot">
        <slot name="merchant-info" />
      </div>
      <CashCoupon
        ref="cashCoupon"
        class="coupon-detail-content"
        :key="couponInfo.couponId"
        :couponInfo="couponInfo"
        :isShowButton="false"
        :spName="couponInfo.merchantInfo.spName"
        :spLogo="couponInfo.merchantInfo.spLogo"
        :isShowSuccToast="isShowSuccToast"
      ></CashCoupon>
      <div class="coupon-detail-list">
        <div class="detail-item" v-for="(item, i) in details" :key="i"  role="text">
          <span class="label">{{ item.label }}</span
          ><span :aria-label="parseCurrencyAria(item.info, false, couponInfo.currencyType)">{{ item.info }}</span>
        </div>
      </div>
      <div class="bottom">
        <CouponBtn
          :state="couponState"
          :text="btnText"
          @click="handleBtnClick"
          v-stat.click="{
            tag: 'wecoupon_module.coupondetail_redeem_click|点击领取',
            ext: {
              fcouponid: couponInfo.couponId,
              fspid: couponInfo.spId,
              fstate: couponState,
              freceiveentry: couponEntry + 'Detail',
              ...getBiExt(),
            },
          }"
          class="detail-btn"
        ></CouponBtn>

        <CouponShare
          v-if="isShowShare"
          class="bottom-share"
          @click="handleShareClick"
          v-stat.click.brow_mult="{
            tag: 'wecoupon_module.coupondetail_share|券分享文字链',
            ext: {
              fcouponid: couponInfo.couponId,
              fspid: couponInfo.spId,
              fstate: couponState,
              ...getBiExt(),
            },
          }"></CouponShare>
      </div>
    </div>
  </FitHalfScreenDialog>
</template>

<script lang="ts">
import { Vue, Component, Ref, Prop } from 'vue-property-decorator';
import { FitHalfScreenDialog } from '@tencent/wphk-ui';
import { getDetailInfo, getCouponBtnText } from './business';
import { CouponInfo, CouponState } from '../../type';
import CashCoupon from '../cash-coupon/index.vue';
import { i18n } from '../../i18n';
import CouponBtn from './button.vue';
import { to } from '@tencent/wphk-base-utils';
import { getJumpPageABTestInfo, getWecouponAdInfo } from '../../business';
import CouponShare from './share.vue';
import { parseCurrencyAria } from '@tencent/wphk-act-base-ui';

@Component({
  components: {
    CouponBtn,
    FitHalfScreenDialog,
    CashCoupon,
    CouponShare,
  },
})
export default class CouponItemDetail extends Vue {
  @Ref('dialog') halfScreenDialog;
  @Ref('cashCoupon') cashCoupon!: CashCoupon;
  /**
   * 是否展示领取成功 toast，默认展示
   */
  @Prop({ default: true }) isShowSuccToast!: boolean;

  /** 当前组件所处的分类 */
  @Prop() classId?: string;
  /** 是否展示分享入口 */
  @Prop() isShowShare?: boolean;
  /** 组件入口，用于上报 */
  @Prop({ default: 'default' }) couponEntry!: string;

  i18n = i18n;

  parseCurrencyAria = parseCurrencyAria;

  // 券信息
  couponInfo: CouponInfo | null = null;

  // 展示半屏详情标示
  isShow = false;

  /**
   * bi ext
   */
  getBiExt() {
    return {
      ...getJumpPageABTestInfo(),
      ...getWecouponAdInfo(),
      fclassid: this.classId,
    };
  }

  /**
   * 券信息详情列表
   */
  get details() {
    return getDetailInfo(this.couponInfo);
  }

  /**
   * 获取按钮文案，因为Normal状态的文案跟默认不一样，所以要重写
   */
  get btnText() {
    if (this.couponState === CouponState.Normal) {
      return i18n('立即領取');
    }
    if (this.couponState === CouponState.Empty) {
      return i18n('已兌罄');
    }

    return getCouponBtnText(this.couponState!);
  }


  get couponState() {
    return this.couponInfo?.state;
  }

  /**
   * 通知调用方领券结果
   */
  notifyAction(name: string, data) {
    this.$emit('action', name, data);
  }

  /**
   * 展示券
   * @param couponInfo 券信息
   */
  show(couponInfo: CouponInfo) {
    this.couponInfo = couponInfo;
    this.isShow = true;
  }

  /**
   * 隐藏半屏
   */
  hide() {
    this.isShow = false;
    this.couponInfo = null;
  }

  handleClose() {
    this.hide();
    this.$emit('close');
  }

  /**
   * 处理按钮点击事件
   */
  async handleBtnClick() {
    const { state } = this.couponInfo!;
    if (state === CouponState.Normal) {
      // 使用cashCoupon里的领券方法
      const [err, res] = await to(this.cashCoupon.getCoupon());
      this.notifyAction('getCoupon', { err, result: res });
    } else if (state === CouponState.Got) {
      // 使用cashCoupon里的用券方法
      this.cashCoupon.useCoupon();
      this.hide();
    }
  }
  /**
   * 分享点击
   */
  handleShareClick() {
    this.$emit('shareClick');
  }
}
</script>

<style lang="less" scoped>
@import '../../less/main.less';
.counpon-detail-dialog {
  // 这里必须要降低半屏弹框的层级，不然会挡住错误提示框
  z-index: 899;

  .coupon-detail-content {
    padding: 24 / @rem 34 / @rem;
    display: flex;
    justify-content: center;
    /deep/ .coupon-wrapper {
      width: 100%;
      .coupon-content {
        width: auto;
        .coupon-right {
          position: static;
        }
      }
    }
  }
  .merchant-info-slot {
    /deep/ .merchant-info {
      justify-content: center;
    }
    /deep/ .merchant-title {
      margin-bottom: 0;
    }
    /deep/ .merchant-value {
      display: none;
    }
  }
  .coupon-detail-list {
    margin-bottom: 74 / @rem;
    .detail-item {
      padding: 8 / @rem 0;
      font-family: 'PingFang HK';
      font-style: normal;

      .label {
        color: #808080;
        opacity: 0.9;
      }
      span {
        font-weight: 400;
        font-size: 28 / @rem;
        line-height: 40 / @rem;
      }
    }
  }
  .bottom {
    margin-bottom: 63 / @rem;
    text-align: center;
    justify-content: center;
    align-items: center;
    .detail-btn {
      display: inline-block;
      font-weight: 500;
      font-size: 34 / @rem;
      line-height: 80 / @rem;
      border-radius: 8 / @rem;
      min-width: 368 / @rem;
      padding: 0;
    }
    &-share {
      margin-top: 24/@rem;
    }
  }
  /deep/ .weui-half-screen-dialog__bd {
    // 針對coupon detail半屏彈窗，為了覆蓋wphk-ui的max-height
    max-height: unset !important;
  }
}
</style>
